/**
 * A/V button for the game header (top-left).
 *
 * New flow (USE_JOIN_SHEET = false):
 *   Always opens the AVControlsSheet with mic/camera toggles.
 *   The channel is already joined in receive-only mode by AVCallProvider.
 *
 * Legacy flow (USE_JOIN_SHEET = true):
 *   Not publishing → JoinCallSheet (pick mode) → AVControlsSheet
 *   Publishing     → AVControlsSheet
 *
 * Sheet coordination:
 *   - Notifies useAVSheetStore so pre-game sheets can dismiss.
 *   - Dismisses when the options drawer opens.
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { Icon } from 'react-native-paper'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { useAVContext, useCallState, useCallActions } from '../../hooks/av'
import type { JoinMode } from '../../hooks/av'
import { AV_CONFIG } from '../../hooks/av/config'
import { JoinCallSheet, type JoinCallSheetRef } from './JoinCallSheet'
import { AVControlsSheet, type AVControlsSheetRef } from './AVControls'
import { useAVSheetStore } from './useAVSheetStore'
import { useGameOptionsDrawerStore } from '../../screens/GameContainer/GameOptionsDrawer'
import { rootStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'

type Props = {
  gameId: Id<'games'> | undefined
  userId: string
}

export function AVButton({ gameId, userId }: Props) {
  const { state: agoraState, actions: agoraActions } = useAVContext()
  const { isPublishing, isMuted, isCameraOn } = useCallState(agoraState)
  const { joinCall, toggleMute, toggleCamera } = useCallActions(gameId, userId, agoraActions, agoraState)

  const joinSheetRef = useRef<JoinCallSheetRef>(null)
  const controlsSheetRef = useRef<AVControlsSheetRef>(null)

  // ─── Sheet coordination ────────────────────────────────────────────

  const handlePress = () => {
    rootStore?.analytics.setEvent(events.av.button_tapped, {
      gameId,
      isPublishing,
      isConvexEnabled: true
    })
    if (AV_CONFIG.USE_JOIN_SHEET) {
      if (isPublishing) {
        controlsSheetRef.current?.open()
      } else {
        joinSheetRef.current?.open()
      }
    } else {
      controlsSheetRef.current?.open()
    }
    rootStore?.analytics.setEvent(events.av.controls_opened, {
      gameId,
      isConvexEnabled: true
    })
    useAVSheetStore.getState().open()
  }

  const handleSheetDismiss = useCallback(() => {
    rootStore?.analytics.setEvent(events.av.controls_dismissed, {
      gameId,
      isConvexEnabled: true
    })
    useAVSheetStore.getState().close()
  }, [gameId])

  // Dismiss AV sheets when the options drawer opens
  const drawerStatus = useGameOptionsDrawerStore((s) => s.status)
  useEffect(() => {
    if (drawerStatus === 'open') {
      controlsSheetRef.current?.close()
      joinSheetRef.current?.close()
      useAVSheetStore.getState().close()
    }
  }, [drawerStatus])

  // ─── Handlers ──────────────────────────────────────────────────────

  const handleJoin = useCallback(
    async (mode: JoinMode) => {
      await joinCall(mode)
    },
    [joinCall]
  )

  const handleToggleMute = useCallback(
    (muted: boolean) => {
      toggleMute(muted)
    },
    [toggleMute]
  )

  const handleToggleCamera = useCallback(
    (cameraOn: boolean) => {
      toggleCamera(cameraOn)
    },
    [toggleCamera]
  )

  const iconName = isPublishing ? 'video' : 'video-off-outline'

  return (
    <>
      <TouchableOpacity onPress={handlePress} style={styles.button}>
        <Icon source={iconName} size={24} color="#fff" />
        {isPublishing && <View style={styles.activeDot} />}
      </TouchableOpacity>

      {AV_CONFIG.USE_JOIN_SHEET && (
        <JoinCallSheet ref={joinSheetRef} onJoin={handleJoin} onDismiss={handleSheetDismiss} />
      )}

      <AVControlsSheet
        ref={controlsSheetRef}
        isMuted={isMuted}
        isCameraOn={isCameraOn}
        onToggleMute={handleToggleMute}
        onToggleCamera={handleToggleCamera}
        onDismiss={handleSheetDismiss}
      />
    </>
  )
}

const styles = StyleSheet.create({
  button: {
    maxHeight: 44,
    padding: 8,
    position: 'relative'
  },
  activeDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50'
  }
})
